<!--
  StatusAwareActionBar.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Renders the action set appropriate to the ApprovalRecord's current
  status (ADR 0039 status-aware action bar). Buttons emit named events
  that the parent panel forwards to the Pinia store -- the bar itself
  is presentation-only.

  Status to action mapping (per ADR 0039 + ADR 0014):
    - awaiting_approval -> Send reminder, Resend request, Escalate, Bypass
    - changes_requested -> Resend to digitizer, Re-request approval
    - rejected          -> Resend to digitizer
    - approved          -> read-only confirmation row, no buttons
    - uploaded          -> Send to digitizer
    - digitizing        -> read-only "at digitizer" status, no buttons

  The "Bypass" CTA opens the ReasonPicker dialog (F1.d's shared
  component). The picker collects a reason + optional notes and the
  parent calls ``store.bypass(recordId, reasonId, notes)``.

  Props:
    - status: string -- the canonical pipeline status to drive the
      action set.
    - pending: boolean -- disables every CTA while a mutation is in
      flight (the store's ``actionPending``).

  Emits (no payload unless noted):
    - send-to-digitizer
    - send-reminder
    - resend-request
    - escalate
    - bypass-open       (parent opens the dialog containing ReasonPicker)
    - resend-digitizer  (changes_requested + rejected resend loop)
    - re-request

  Bypass intentionally does NOT carry a reason here -- the picker dialog
  is rendered in the parent so it can dim the right pane consistently
  with other modal flows. The "bypass-open" emit is the contract.
-->
<template>
    <div class="aw-actions" data-testid="status-aware-action-bar">
        <span class="aw-actions__label">Actions</span>
        <template v-if="status === 'uploaded'">
            <button
                type="button"
                class="aw-actions__btn aw-actions__btn--primary"
                :disabled="pending"
                data-testid="action-send-to-digitizer"
                @click="$emit('send-to-digitizer')"
            >
                Send to digitizer
            </button>
        </template>
        <template v-else-if="status === 'digitizing'">
            <span class="aw-actions__readonly">
                Awaiting digitized artwork from the digitizer.
            </span>
        </template>
        <template v-else-if="status === 'awaiting_approval'">
            <button
                type="button"
                class="aw-actions__btn aw-actions__btn--primary"
                :disabled="pending"
                data-testid="action-send-reminder"
                @click="$emit('send-reminder')"
            >
                Send reminder
            </button>
            <button
                type="button"
                class="aw-actions__btn"
                :disabled="pending"
                data-testid="action-resend-request"
                @click="$emit('resend-request')"
            >
                Resend approval request
            </button>
            <button
                type="button"
                class="aw-actions__btn aw-actions__btn--warn"
                :disabled="pending"
                data-testid="action-escalate"
                @click="$emit('escalate')"
            >
                Escalate to account owner
            </button>
            <span class="aw-actions__spacer"></span>
            <button
                type="button"
                class="aw-actions__btn"
                :disabled="pending"
                data-testid="action-bypass"
                @click="$emit('bypass-open')"
            >
                Bypass approval
            </button>
        </template>
        <template v-else-if="status === 'changes_requested'">
            <button
                type="button"
                class="aw-actions__btn aw-actions__btn--primary"
                :disabled="pending"
                data-testid="action-resend-digitizer"
                @click="$emit('resend-digitizer')"
            >
                Resend to digitizer with corrections
            </button>
            <button
                type="button"
                class="aw-actions__btn"
                :disabled="pending"
                data-testid="action-re-request"
                @click="$emit('re-request')"
            >
                Re-request approval
            </button>
        </template>
        <template v-else-if="status === 'rejected'">
            <button
                type="button"
                class="aw-actions__btn aw-actions__btn--primary"
                :disabled="pending"
                data-testid="action-resend-digitizer"
                @click="$emit('resend-digitizer')"
            >
                Resend to digitizer with corrections
            </button>
        </template>
        <template v-else-if="status === 'approved'">
            <span
                class="aw-actions__readonly aw-actions__readonly--ok"
                data-testid="action-approved-readonly"
            >
                Approved. Cleared for production.
            </span>
        </template>
    </div>
</template>

<script>
export default {
    name: 'StatusAwareActionBar',
    props: {
        status: {
            type: String,
            required: true,
        },
        pending: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'send-to-digitizer',
        'send-reminder',
        'resend-request',
        'escalate',
        'bypass-open',
        'resend-digitizer',
        're-request',
    ],
}
</script>

<style scoped>
.aw-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px 14px;
    background: var(--tf-surface-alt, #f7fafc);
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    margin-bottom: 16px;
}
.aw-actions__label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tf-fg-4, #5a6a82);
    margin-right: 4px;
}
.aw-actions__spacer {
    flex: 1;
}
.aw-actions__btn {
    border: 1px solid var(--tf-border-strong, #cbd5e0);
    background: var(--tf-bg, #ffffff);
    color: var(--tf-fg-1, #1a202c);
    padding: 6px 12px;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
}
.aw-actions__btn:hover:not(:disabled) {
    background: var(--tf-surface-alt, #edf2f7);
}
.aw-actions__btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
.aw-actions__btn--primary {
    background: var(--tf-primary, #1976d2);
    color: #ffffff;
    border-color: var(--tf-primary, #1976d2);
}
.aw-actions__btn--primary:hover:not(:disabled) {
    background: var(--tf-primary-press, #0d47a1);
}
.aw-actions__btn--warn {
    background: #fff3df;
    border-color: #c97900;
    color: #7a4f00;
}
.aw-actions__readonly {
    font-size: 13px;
    color: var(--tf-fg-3, #4a5568);
}
.aw-actions__readonly--ok {
    color: #2e7d32;
    font-weight: 500;
}
</style>
